/* RESPONSIBLE TEAM: team-standalone */
import BaseValidator from 'ember-cp-validations/validators/base';
import { inject as service } from '@ember/service';
import type AppService from 'embercom/services/app-service';
import type FinStandaloneService from 'embercom/services/fin-standalone-service';
import type IntlService from 'ember-intl/services/intl';
import type Store from '@ember-data/store';
import StandardizedPlatform from 'embercom/objects/standalone/platforms/standardized-platform';

const CONVERSATION_CUSTOM_ATTRIBUTE_PREFIX = 'conversation.custom_attribute.';

export default class PredicateFieldsAvailableInProduction extends BaseValidator {
  @service declare appService: AppService;
  @service declare finStandaloneService: FinStandaloneService;
  @service declare intl: IntlService;
  @service declare store: Store;

  validate(predicateGroup: any, options: { messageKey: string }) {
    if (!this.appService.app.canUseStandaloneMultiEnv) {
      return true;
    }

    let platform = this.finStandaloneService.platform;
    if (!platform || !(platform instanceof StandardizedPlatform)) {
      return true;
    }

    if (platform.isLoadingDataFields) {
      return true;
    }

    if (!predicateGroup?.predicates) {
      return true;
    }

    for (let i = 0; i < predicateGroup.predicates.length; i++) {
      let predicate = predicateGroup.predicates.objectAt(i);
      if (this.hasUnavailableField(predicate, platform)) {
        return this.intl.t(options.messageKey);
      }
    }

    return true;
  }

  private hasUnavailableField(predicate: any, platform: StandardizedPlatform): boolean {
    // Recurse into nested logical predicates (AND/OR groups)
    if (predicate.predicates?.length) {
      for (let i = 0; i < predicate.predicates.length; i++) {
        let nested = predicate.predicates.objectAt(i);
        if (this.hasUnavailableField(nested, platform)) {
          return true;
        }
      }
    }

    let attribute = predicate.attribute;
    if (!attribute || !attribute.startsWith(CONVERSATION_CUSTOM_ATTRIBUTE_PREFIX)) {
      return false;
    }

    let descriptorId = attribute.split('.')[2];
    if (!descriptorId) {
      return false;
    }

    let descriptor = this.store.peekRecord('conversation-attributes/descriptor', descriptorId);
    if (!descriptor || !descriptor.isStandaloneSyncedAttribute || descriptor.archived) {
      return false;
    }

    return !platform.isMappedConversationAttribute(attribute);
  }

  static getDependentsFor() {
    return [];
  }
}
